import { RefObject, useEffect } from 'react';

// SPA navigation doesn't move focus or announce the new page by itself.
// App marks every in-app route change (not the first load); the next page
// frame to mount then moves focus to its <h1>, so keyboard and screen-reader
// users start at the top of the new view. The heading gets no visible ring:
// it isn't a control (styles.css).
let pendingFocus = false;

export function markNavigation() {
  pendingFocus = true;
}

export function useFocusHeadingOnNavigate(ref: RefObject<HTMLElement>) {
  useEffect(() => {
    if (!pendingFocus || !ref.current) return;
    pendingFocus = false;
    ref.current.focus();
  }, [ref]);
}

/**
 * For sign-in steps with one job (school code, email, two-factor code, a new
 * password): put the cursor straight into the field, on first load too. Its
 * label names it for screen readers and the page title changes with the step.
 * Without a field, falls back to the heading on in-app navigation. `step`
 * re-runs it when the same frame shows a new step.
 */
export function useFocusFieldOrHeading(
  heading: RefObject<HTMLElement>,
  field: RefObject<HTMLElement> | undefined,
  step?: unknown,
) {
  useEffect(() => {
    if (field?.current) {
      pendingFocus = false;
      field.current.focus();
      return;
    }
    if (!pendingFocus || !heading.current) return;
    pendingFocus = false;
    heading.current.focus();
  }, [heading, field, step]);
}
